/**
 * View Calculation Panel - CFO-Ready Value Breakdown
 *
 * Displays detailed value calculation with:
 * 1. Pistoia driver info and formula
 * 2. Evidence chain (parameter → Bridge source)
 * 3. Validation warnings
 * 4. Confidence/attribution explanations
 * 5. Station data source indicators
 *
 * @see Documentation/Development/Design Docs/Bridge AI to Action Plan/
 */

import React, { useState } from 'react';
import type {
  ValueCalculationDetails,
  EvidenceChain,
  ValidationResult,
  ConfidenceInfo,
  AttributionInfo,
  StationSource,
  PistoiaDriverInfo,
} from '../types/actionPlan';
import { formatCurrency } from './actionPlanValueFormat';

// ============================================================================
// COMPONENT PROPS
// ============================================================================

interface ViewCalculationPanelProps {
  calculation: ValueCalculationDetails;
  taskTitle: string;
  isExpanded?: boolean;
  onToggle?: () => void;
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

// Local `formatCurrency` removed (S4g): it returned '$0' for an absent value —
// and the repository's legacy fill delivers `npv_3yr_usd: null` for derivations
// that were never stored, so this panel was rendering a fabricated "$0" NPV.
// The shared implementation answers "Unavailable", which is what is true.

/**
 * The NPV horizon and discount rate, WHEN the wire recorded them.
 *
 * Pistoia-calculated plans carry `evidence_chain.calculations.npv_parameters`
 * ({years, discount_rate}); other producers do not. The rate is shown only when
 * it is actually present — a hardcoded "10%" would misdescribe any plan priced
 * by a different path.
 */
function npvParametersOf(
  calculation: ValueCalculationDetails,
): { years: number; ratePct: number } | null {
  const chain: unknown = calculation.evidence_chain;
  if (typeof chain !== 'object' || chain === null) return null;
  const calcs = (chain as Record<string, unknown>).calculations;
  if (typeof calcs !== 'object' || calcs === null) return null;
  const params = (calcs as Record<string, unknown>).npv_parameters;
  if (typeof params !== 'object' || params === null) return null;
  const years = (params as Record<string, unknown>).years;
  const rate = (params as Record<string, unknown>).discount_rate;
  if (typeof years !== 'number' || !Number.isFinite(years) || years <= 0) return null;
  if (typeof rate !== 'number' || !Number.isFinite(rate) || rate < 0) return null;
  return { years, ratePct: rate * 100 };
}

/** D5 wording — the NPV is a derived view, never a second independent estimate. */
function npvDerivationNote(calculation: ValueCalculationDetails): string {
  const params = npvParametersOf(calculation);
  if (params) {
    return (
      `Derived from the annual task forecast over ${params.years} years at a ` +
      `${params.ratePct.toFixed(0)}% annual discount rate. It is a derived view of the ` +
      'same forecast basis, not a second independent estimate.'
    );
  }
  return (
    'Derived from the annual task forecast using the calculator’s configured ' +
    'horizon and discount rate. It is a derived view of the same forecast basis, ' +
    'not a second independent estimate.'
  );
}

const getConfidenceColor = (level: string): string => {
  switch (level) {
    case 'High': return '#10b981'; // green
    case 'Medium': return '#f59e0b'; // amber
    case 'Low': return '#ef4444'; // red
    default: return '#6b7280'; // gray
  }
};

const getSeverityIcon = (severity: string): string => {
  switch (severity) {
    case 'error': return '❌';
    case 'warning': return '⚠️';
    case 'info': return 'ℹ️';
    default: return '•';
  }
};

const getSourceTypeIcon = (sourceType: string): string => {
  switch (sourceType) {
    case 'bridge_recommendation': return '🧠';
    case 'station_data': return '📊';
    case 'organization_context': return '🏢';
    case 'default': return '📋';
    default: return '•';
  }
};

const getImpactIcon = (impact: string): string => {
  switch (impact) {
    case 'positive': return '✅';
    case 'negative': return '⚠️';
    case 'neutral': return '➖';
    default: return '•';
  }
};

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

/**
 * Pistoia Driver Section
 */
const PistoiaDriverSection: React.FC<{ driver: PistoiaDriverInfo }> = ({ driver }) => (
  <div className="calc-section pistoia-driver-section">
    <div className="section-header">
      <span className="section-icon">📐</span>
      <h5>Pistoia Alliance Calculation</h5>
    </div>
    <div className="driver-info">
      <div className="driver-badge">
        <span className="driver-name">{driver.driver_name}</span>
        <span className="driver-category">{driver.driver_category}</span>
      </div>
      <div className="driver-formula">
        <span className="formula-label">Formula:</span>
        <code className="formula-code">{driver.formula}</code>
      </div>
      {driver.parameters && Object.keys(driver.parameters).length > 0 && (
        <div className="driver-parameters">
          <span className="params-label">Parameters Used:</span>
          <div className="params-grid">
            {Object.entries(driver.parameters).map(([key, value]) => (
              <div key={key} className="param-item">
                <span className="param-name">{key.replace(/_/g, ' ')}</span>
                <span className="param-value">
                  {typeof value === 'number' ? value.toLocaleString() : String(value)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  </div>
);

/**
 * Evidence Chain Section
 */
const EvidenceChainSection: React.FC<{ chain: EvidenceChain }> = ({ chain }) => (
  <div className="calc-section evidence-chain-section">
    <div className="section-header">
      <span className="section-icon">🔗</span>
      <h5>Evidence Chain</h5>
      <span className="bridge-ref">Bridge Rec: {chain.bridge_recommendation_id}</span>
    </div>

    {/* Parameter Sources */}
    <div className="evidence-subsection">
      <h6>Parameter Sources</h6>
      <div className="source-list">
        {chain.parameter_sources.map((source, i) => (
          <div key={i} className={`source-item source-${source.source_type}`}>
            <div className="source-header">
              <span className="source-icon">{getSourceTypeIcon(source.source_type)}</span>
              <span className="source-param">{source.parameter_name}</span>
              <span className="source-value">{String(source.value)}</span>
            </div>
            <div className="source-description">{source.source_description}</div>
            {source.source_location && (
              <div className="source-location">
                <code>{source.source_location}</code>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>

    {/* Calculation Steps */}
    <div className="evidence-subsection">
      <h6>Calculation Steps</h6>
      <div className="steps-list">
        {chain.calculation_steps.map((step, i) => (
          <div key={i} className="calc-step">
            <span className="step-number">{step.step_number}</span>
            <div className="step-content">
              <div className="step-description">{step.description}</div>
              <div className="step-formula">
                <code>{step.formula}</code>
                <span className="step-result">
                  = {step.result.toLocaleString()}{step.unit ? ` ${step.unit}` : ''}
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

/**
 * Validation Warnings Section
 */
const ValidationSection: React.FC<{ validation: ValidationResult }> = ({ validation }) => (
  <div className={`calc-section validation-section ${validation.is_valid ? 'valid' : 'has-warnings'}`}>
    <div className="section-header">
      <span className="section-icon">{validation.is_valid ? '✅' : '⚠️'}</span>
      <h5>Validation Status</h5>
      <span className={`validation-badge ${validation.is_valid ? 'valid' : 'warning'}`}>
        {validation.is_valid ? 'All Checks Passed' : `${validation.warnings.length} Warning(s)`}
      </span>
    </div>

    {validation.warnings.length > 0 && (
      <div className="warnings-list">
        {validation.warnings.map((warning, i) => (
          <div key={i} className={`warning-item severity-${warning.severity}`}>
            <div className="warning-header">
              <span className="warning-icon">{getSeverityIcon(warning.severity)}</span>
              <span className="warning-rule">{warning.rule}</span>
            </div>
            <div className="warning-message">{warning.message}</div>
            {warning.suggestion && (
              <div className="warning-suggestion">
                <strong>Suggestion:</strong> {warning.suggestion}
              </div>
            )}
          </div>
        ))}
      </div>
    )}

    {validation.passed_rules.length > 0 && (
      <div className="passed-rules">
        <span className="passed-label">Passed:</span>
        <div className="rules-chips">
          {validation.passed_rules.map(rule => (
            <span key={rule} className="rule-chip passed">{rule}</span>
          ))}
        </div>
      </div>
    )}
  </div>
);

/**
 * Confidence Info Section
 */
const ConfidenceSection: React.FC<{ confidence: ConfidenceInfo }> = ({ confidence }) => (
  <div className="calc-section confidence-section">
    <div className="section-header">
      <span className="section-icon">📊</span>
      <h5>Confidence Level</h5>
      <span
        className="confidence-badge"
        style={{ backgroundColor: getConfidenceColor(confidence.level) }}
      >
        {confidence.level} ({(confidence.multiplier * 100).toFixed(0)}%)
      </span>
    </div>

    <div className="confidence-explanation">{confidence.explanation}</div>

    <div className="confidence-factors">
      <h6>Contributing Factors</h6>
      <div className="factors-list">
        {confidence.factors.map((factor, i) => (
          <div key={i} className={`factor-item impact-${factor.impact}`}>
            <span className="factor-icon">{getImpactIcon(factor.impact)}</span>
            <span className="factor-name">{factor.factor_name}:</span>
            <span className="factor-value">{factor.value}</span>
            <span className="factor-source">({factor.source})</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

/**
 * Attribution Info Section
 */
const AttributionSection: React.FC<{ attribution: AttributionInfo }> = ({ attribution }) => (
  <div className="calc-section attribution-section">
    <div className="section-header">
      <span className="section-icon">🎯</span>
      <h5>FAIR Attribution</h5>
      <span className="attribution-badge">{attribution.percentage}%</span>
    </div>

    <div className="attribution-explanation">{attribution.explanation}</div>

    <div className="complexity-breakdown">
      <h6>Implementation Complexity</h6>
      <div className="complexity-list">
        {attribution.complexity_breakdown.map((item, i) => (
          <div key={i} className={`complexity-item complexity-${item.complexity}`}>
            <span className={`complexity-dot ${item.complexity}`}></span>
            <span className="complexity-step">{item.step}</span>
            <span className="complexity-level">{item.complexity}</span>
            {item.rationale && (
              <span className="complexity-rationale">{item.rationale}</span>
            )}
          </div>
        ))}
      </div>
    </div>

    <div className="attribution-visual">
      <div className="attribution-bar">
        <div
          className="attribution-fill"
          style={{ width: `${attribution.percentage}%` }}
        ></div>
      </div>
      <div className="attribution-legend">
        <span>FAIR: {attribution.percentage}%</span>
        <span>Other factors: {100 - attribution.percentage}%</span>
      </div>
    </div>
  </div>
);

/**
 * Station Sources Section
 */
const StationSourcesSection: React.FC<{ sources: StationSource[] }> = ({ sources }) => (
  <div className="calc-section station-sources-section">
    <div className="section-header">
      <span className="section-icon">🔬</span>
      <h5>Data Sources</h5>
    </div>

    <div className="stations-grid">
      {sources.map((source, i) => (
        <div key={i} className="station-source-card">
          <div className="station-header">
            <span className="station-icon">{source.station_icon}</span>
            <span className="station-name">{source.station_name}</span>
          </div>
          <div className="station-contribution">{source.contribution}</div>
          {source.data_point && (
            <div className="station-datapoint">
              <code>{source.data_point}</code>
            </div>
          )}
        </div>
      ))}
    </div>
  </div>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const ViewCalculationPanel: React.FC<ViewCalculationPanelProps> = ({
  calculation,
  isExpanded = false,
  onToggle,
}) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'evidence' | 'validation'>('overview');

  // Check what data is available
  // Note: pistoia_driver can be a string (driver name) or object (full driver info)
  // Only treat as "enhanced" if it's an object with the required structure
  const hasPistoiaDriverObject = calculation.pistoia_driver &&
    typeof calculation.pistoia_driver === 'object' &&
    'driver_name' in calculation.pistoia_driver;
  const hasEvidenceChain = !!calculation.evidence_chain?.parameter_sources;
  const hasValidation = !!calculation.validation_result;
  const hasConfidence = !!calculation.confidence_info?.level;
  const hasAttribution = !!calculation.attribution_info;
  const hasStationSources = calculation.station_sources && calculation.station_sources.length > 0;

  // If no enhanced data, show legacy view (fair_improvements)
  const hasEnhancedData = hasPistoiaDriverObject || (hasEvidenceChain && hasValidation);

  if (!hasEnhancedData) {
    // Legacy view: just show fair_improvements
    return (
      <div className="view-calculation-panel legacy">
        {/* A real disclosure button (S4h): the header was a clickable div —
            unreachable by keyboard and silent about its expanded state. */}
        <button
          type="button"
          className="calc-header"
          onClick={onToggle}
          aria-expanded={isExpanded}
        >
          <span className="calc-icon" aria-hidden="true">💰</span>
          <span className="calc-title">Value Breakdown</span>
          {/* The figure is a 3-yr NPV — named, so it cannot be read as the annual
              forecast shown on the card one line up (S4g). */}
          <span className="calc-value">
            <span className="calc-value__measure">3-yr NPV</span>{' '}
            {formatCurrency(calculation.npv_3yr_usd)}
          </span>
        </button>

        {isExpanded && (
          <div className="calc-body">
            {calculation.fair_improvements.map((improvement, i) => (
              <div key={i} className="improvement-item">
                <div className="improvement-header">
                  <span className="improvement-rule">{improvement.rule}</span>
                  <span className="improvement-driver">{improvement.driver}</span>
                  <span className="improvement-value">
                    {formatCurrency(improvement.annual_value_usd)}/year
                  </span>
                </div>
                <div className="improvement-states">
                  <span className="state current">{improvement.current_state}</span>
                  <span className="state-arrow">→</span>
                  <span className="state improved">{improvement.improved_state}</span>
                </div>
                <div className="improvement-calc">{improvement.calculation}</div>
              </div>
            ))}
            <div className="calc-total">
              <span>3-Year NPV:</span>
              <span className="total-amount">{formatCurrency(calculation.npv_3yr_usd)}</span>
            </div>
            <p className="ap-npv-note">{npvDerivationNote(calculation)}</p>
          </div>
        )}
      </div>
    );
  }

  // Enhanced view with all features
  return (
    <div className="view-calculation-panel enhanced">
      {/* A real disclosure button (S4h), same contract as the legacy header. */}
      <button
        type="button"
        className="calc-header"
        onClick={onToggle}
        aria-expanded={isExpanded}
      >
        <span className="calc-icon" aria-hidden="true">📊</span>
        {/* "View Calculation" was implementation vocabulary — the panel shows how
            the task's value was derived, so it says that (S4g). */}
        <span className="calc-title">Value derivation</span>
        {hasValidation && !calculation.validation_result?.is_valid && (
          <span className="warning-indicator" role="img" aria-label="Validation warnings">⚠️</span>
        )}
        <span className="calc-value">
          <span className="calc-value__measure">3-yr NPV</span>{' '}
          {formatCurrency(calculation.npv_3yr_usd)}
        </span>
        <span className="calc-chevron" aria-hidden="true">{isExpanded ? '▼' : '▶'}</span>
      </button>

      {isExpanded && (
        <div className="calc-body">
          {/* Tab Navigation */}
          <div className="calc-tabs">
            <button
              className={`calc-tab ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveTab('overview')}
            >
              Overview
            </button>
            {hasEvidenceChain && (
              <button
                className={`calc-tab ${activeTab === 'evidence' ? 'active' : ''}`}
                onClick={() => setActiveTab('evidence')}
              >
                Evidence Chain
              </button>
            )}
            {hasValidation && (
              <button
                className={`calc-tab ${activeTab === 'validation' ? 'active' : ''}`}
                onClick={() => setActiveTab('validation')}
              >
                Validation
                {!calculation.validation_result?.is_valid && (
                  <span className="tab-badge">!</span>
                )}
              </button>
            )}
          </div>

          {/* Tab Content */}
          <div className="calc-tab-content">
            {activeTab === 'overview' && (
              <div className="overview-tab">
                {/* Summary Card */}
                <div className="calc-summary-card">
                  <div className="summary-row">
                    <span className="summary-label">Base Value</span>
                    <span className="summary-value">{formatCurrency(calculation.base_value_usd)}/year</span>
                  </div>
                  {hasConfidence && (
                    <div className="summary-row">
                      <span className="summary-label">Confidence Adjustment</span>
                      <span className="summary-value">
                        × {(calculation.confidence_info!.multiplier * 100).toFixed(0)}%
                      </span>
                    </div>
                  )}
                  {hasAttribution && (
                    <div className="summary-row">
                      <span className="summary-label">FAIR Attribution</span>
                      <span className="summary-value">
                        × {calculation.attribution_info!.percentage}%
                      </span>
                    </div>
                  )}
                  <div className="summary-row total">
                    {/* This row IS the task's annual forecast — the same measure the
                        card badge and the drawer's "Annual forecast" show. */}
                    <span className="summary-label">Risk-adjusted annual forecast</span>
                    <span className="summary-value">{formatCurrency(calculation.total_value_usd)}/year</span>
                  </div>
                  <div className="summary-row npv">
                    <span className="summary-label">3-Year NPV</span>
                    <span className="summary-value highlight">{formatCurrency(calculation.npv_3yr_usd)}</span>
                  </div>
                  {/* The annual forecast and the NPV coexist here, so their
                      relationship is stated rather than left to inference (D5). */}
                  <p className="ap-npv-note">{npvDerivationNote(calculation)}</p>
                </div>

                {/* Pistoia Driver */}
                {hasPistoiaDriverObject && (
                  <PistoiaDriverSection driver={calculation.pistoia_driver as PistoiaDriverInfo} />
                )}

                {/* Confidence Info */}
                {hasConfidence && (
                  <ConfidenceSection confidence={calculation.confidence_info!} />
                )}

                {/* Attribution Info */}
                {hasAttribution && (
                  <AttributionSection attribution={calculation.attribution_info!} />
                )}

                {/* Station Sources */}
                {hasStationSources && (
                  <StationSourcesSection sources={calculation.station_sources!} />
                )}
              </div>
            )}

            {activeTab === 'evidence' && hasEvidenceChain && (
              <EvidenceChainSection chain={calculation.evidence_chain!} />
            )}

            {activeTab === 'validation' && hasValidation && (
              <ValidationSection validation={calculation.validation_result!} />
            )}
          </div>

          {/* CFO Summary Footer */}
          <div className="calc-footer">
            <div className="cfo-summary">
              <span className="cfo-label">CFO Summary:</span>
              <span className="cfo-text">{calculation.formula_trace}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewCalculationPanel;
